import type {
  CreateContractItemMutationOptions,
  CreateContractItemMutationVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import ToastService from '~/services/toast.service';

import { i18n } from '~/utils';

import { queryKeysContract } from '../queryKeys';

import { createContractItem } from './createContractItem';

/**
 * Custom hook for creating a new line item for a contract. The item is
 * persisted immediately, which the success toast tells the user.
 *
 * @param options - Additional mutation options; callbacks run after the hook's own.
 * @returns The mutation; `isPending` stays true until the contract and its item lists have refetched.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/create_contract_item_contracts__contract_id__items_post
 */
export const useMutationCreateContractItem = (
  options?: CreateContractItemMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async ({
      contractId,
      payload,
    }: CreateContractItemMutationVariables) =>
      createContractItem(contractId, payload),
    async onSettled(data, error, variables, onMutateResult, context) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.getItems(variables.contractId, undefined),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.get(variables.contractId),
        }),
        options?.onSettled?.(data, error, variables, onMutateResult, context),
      ]);
    },
    async onSuccess(data, variables, onMutateResult, context) {
      ToastService.success(
        i18n.t(($) => $.contractItem.createContractItem.success, {
          ns: 'data',
        }),
      );

      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
